import axios, { AxiosError } from "axios";
import { useCallback, useContext } from "react";
import useSWR from "swr";
import { AuthContext } from "../../../context/AuthContext";
import { AdminEndpointEnum } from "../../../enums/endpoints";
import { ADMIN_API_URL } from "../../../utils/env";
import { ToastEnum, ToastStatusEnum, useToast } from "../../toast";

export interface IRewardVolumeAllocation {
  account: string;
  basis_points: number;
}

export interface IRewardVolumeAllocationRule {
  source_account: string;
  allocations: IRewardVolumeAllocation[];
}

interface IRewardVolumeAllocationsResponse {
  rules: IRewardVolumeAllocationRule[];
}

interface IRewardVolumeAllocationMutationResponse {
  success: boolean;
}

export const useRewardVolumeAllocations = () => {
  const { queryConfig, tokenId } = useContext(AuthContext);
  const { addToast } = useToast();
  const endpoint = ADMIN_API_URL(AdminEndpointEnum.REWARD_VOLUME_ALLOCATIONS);

  const swr = useSWR<IRewardVolumeAllocationsResponse, AxiosError>(
    tokenId ? [endpoint, tokenId] : undefined,
    async () => {
      const response = await axios.get<
        Partial<IRewardVolumeAllocationsResponse>
      >(endpoint, queryConfig());
      return { rules: response.data?.rules ?? [] };
    },
    { refreshInterval: 30000 }
  );

  const withAuthHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${tokenId}` } }),
    [tokenId]
  );

  const showError = useCallback(
    (error: unknown) => {
      // eslint-disable-next-line no-console
      console.error(error);
      const payload = (error as AxiosError)?.response?.data as any;
      addToast(
        {
          type: ToastEnum.SIMPLE,
          header: "Reward volume allocation update failed",
          subheader:
            payload?.error ||
            payload?.message ||
            (error instanceof Error ? error.message : "Unknown error"),
          status: ToastStatusEnum.ERROR,
        },
        5000
      );
    },
    [addToast]
  );

  const updateRewardVolumeAllocation = useCallback(
    async (rule: IRewardVolumeAllocationRule): Promise<boolean> => {
      try {
        const response =
          await axios.post<IRewardVolumeAllocationMutationResponse>(
            endpoint,
            rule,
            withAuthHeaders()
          );
        if (response.data?.success !== true) {
          throw new Error("Backend rejected the allocation update");
        }
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Reward volume allocation updated",
            subheader: `${rule.allocations.length} destination${
              rule.allocations.length === 1 ? "" : "s"
            } configured for ${rule.source_account}`,
            status: ToastStatusEnum.SUCCESS,
          },
          4000
        );
        await swr.mutate();
        return true;
      } catch (error) {
        showError(error);
        return false;
      }
    },
    [addToast, endpoint, showError, swr, withAuthHeaders]
  );

  const deleteRewardVolumeAllocation = useCallback(
    async (sourceAccount: string): Promise<boolean> => {
      try {
        const response =
          await axios.post<IRewardVolumeAllocationMutationResponse>(
            endpoint,
            { source_account: sourceAccount, allocations: [] },
            withAuthHeaders()
          );
        if (response.data?.success !== true) {
          throw new Error("Backend rejected the allocation removal");
        }
        addToast(
          {
            type: ToastEnum.SIMPLE,
            header: "Reward volume allocation removed",
            subheader: `Future calculations leave volume on ${sourceAccount}`,
            status: ToastStatusEnum.SUCCESS,
          },
          4000
        );
        await swr.mutate();
        return true;
      } catch (error) {
        showError(error);
        return false;
      }
    },
    [addToast, endpoint, showError, swr, withAuthHeaders]
  );

  return {
    ...swr,
    updateRewardVolumeAllocation,
    deleteRewardVolumeAllocation,
  };
};
